import { Component, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { CheckoutActions } from '@app/pages/checkout-app/checkout-store/actions';
import { CheckoutReducer } from '@app/pages/checkout-app/checkout-store/reducers';
import { LockableTabItem } from '@app/services/feature-access/feature-access.model';
import { FeatureAccessService } from '@app/services/feature-access/feature-access.service';
import { DropdownOption } from '@app/storybook/components/dropdown/dropdown.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import * as fromCheckoutReducer from '@pages/checkout-app/checkout-store/reducers/checkout.reducer';
import {
  CheckoutContentExtension,
  ExtensionComponentType,
  ICheckoutExtensions,
} from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { generateRandomString } from '@utils/string-utils';
import { ViewMode } from '../../../models';
import {
  blockModeOptions,
  blockTypeOptions,
  borderTypeOptions,
  borderWidthOptions,
  cornerRadiusOptions,
  getBlockImageSrc,
  paddingOptions,
  styleOptions,
} from './checkout-content.utils';
import { customDynamicFieldValidator } from '../../shared/validators/dynamic-fields.validator';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import { getExtensionRules } from '../../../shared/all-extension-rules';
import { ExtensionsService } from '@pages/checkout-app/services/extensions/extensions.service';
import { getDynamicColumnOptions } from '@utils/table.utils';
import { PlacementModeConfig } from '@pages/checkout-app/checkout-layout/main/components/extensions/components/shared/config/config';
import { ShopService } from '@app/services/shops/shop.service';
import { Observable } from 'rxjs';
import { Shop } from '@app/services/shops/shop.model';
import { MatDialog } from '@angular/material/dialog';
import { IframeDialogComponent } from '@app/sharedModule/dialogs/iframe-dialog/iframe-dialog.component';

enum CheckoutContentTab {
  SETTINGS = 'settings',
  TIMER = 'timer',
  DISPLAY_CONDITIONS = 'displayConditions',
}

@Component({
  selector: 'checkout-content',
  templateUrl: './checkout-content.component.html',
  styleUrls: ['./checkout-content.component.scss'],
  standalone: false,
})
@UntilDestroy()
export class CheckoutContentComponent implements OnInit {
  defaultIconUrl = `${window.location.origin}/assets/icons/checkout_content_default.png`;
  formGroup: UntypedFormGroup;
  loadedExtension$ = this.store.select(CheckoutReducer.selectActiveExtension);
  loadedShippingMethods$ = this.store.select(CheckoutReducer.selectShippingMethods);
  loadedExtension: ICheckoutExtensions;
  usageTokenControl: UntypedFormControl;
  BlockType = CheckoutContentExtension.BlockType;
  cornerRadiusOptions = cornerRadiusOptions;
  blockTypeOptions: DropdownOption[] = blockTypeOptions;
  blockModeOptions: DropdownOption[] = blockModeOptions;
  styleOptions: DropdownOption[] = styleOptions;
  borderTypeOptions = borderTypeOptions;
  StyleType = CheckoutContentExtension.StyleType;
  borderWidthOptions: DropdownOption[] = borderWidthOptions;
  paddingOptions = paddingOptions;
  placementModeConfig = [...PlacementModeConfig];
  MIN_BLOCKS = CheckoutContentExtension.MIN_BLOCKS;
  MAX_BLOCKS = CheckoutContentExtension.MAX_BLOCKS;
  dynamicFieldsAutocompleteOptions$ = this.extensionsService.dynamicFieldsAutocomplete$;
  shop$: Observable<Shop>;
  shopCurrencySymbol: string;
  restrictionsRules = getExtensionRules();
  CheckoutContentTab = CheckoutContentTab;
  activeTab = CheckoutContentTab.SETTINGS;
  tabs: LockableTabItem<CheckoutContentTab>[] = [
    { label: 'Settings', value: CheckoutContentTab.SETTINGS },
    { label: 'Timer', value: CheckoutContentTab.TIMER },
    { label: 'Display Conditions', value: CheckoutContentTab.DISPLAY_CONDITIONS },
  ];

  constructor(
    private shopService: ShopService,
    private store: Store<fromCheckoutReducer.State>,
    private fb: UntypedFormBuilder,
    private rulesDynamicFormService: RulesDynamicFormService,
    private extensionsService: ExtensionsService,
    private dialog: MatDialog,
    private featureAccess: FeatureAccessService,
  ) {}

  ngOnInit(): void {
    this.tabs = this.featureAccess.lockTabOnFreePlan(this.tabs, CheckoutContentTab.DISPLAY_CONDITIONS);
    this.extensionsService.setCurrentExtensionType(ExtensionComponentType.CHECKOUT_CONTENT);

    this.extensionsService.save$.pipe(untilDestroyed(this)).subscribe(() => {
      this.handleSave();
    });

    this.initSubscriptions();
    this.shop$ = this.shopService.shop$;
    this.shopCurrencySymbol = this.shopService.shopCurrencySymbol;
  }

  private initSubscriptions() {
    this.loadedExtension$.pipe(untilDestroyed(this)).subscribe((extension: ICheckoutExtensions) => {
      this.loadedExtension = extension;
      this.initForm(extension);
    });

    this.formGroup
      .get('numOfBlocks')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((numOfBlocks) => this.handleNumOfBlocksChange(numOfBlocks));

    this.initChangesForExampleView();
  }

  private handleNumOfBlocksChange(numOfBlocks: number) {
    const [defaultBlock] = CheckoutContentExtension.templateDefaultValue.blocks;
    const blocksArray = this.formGroup.get('blocks') as UntypedFormArray;
    const blocksDelta = numOfBlocks - blocksArray.length;

    for (let i = 0; i < Math.abs(blocksDelta); i++) {
      blocksDelta > 0
        ? blocksArray.push(this.createBlockControl(defaultBlock))
        : blocksArray.removeAt(blocksArray.length - 1);
    }
  }

  private createBlockControl(block: CheckoutContentExtension.Block) {
    switch (block.type) {
      case CheckoutContentExtension.BlockType.IMAGE:
        const initialImage = getBlockImageSrc(<CheckoutContentExtension.ImageBlock>block) ?? this.defaultIconUrl;
        return new UntypedFormGroup({
          image: new UntypedFormControl(initialImage),
          type: new UntypedFormControl(CheckoutContentExtension.BlockType.IMAGE),
          imageContainerWidth: new UntypedFormControl((<CheckoutContentExtension.ImageBlock>block).imageContainerWidth),
          imageContainerHeight: new UntypedFormControl(
            (<CheckoutContentExtension.ImageBlock>block).imageContainerHeight,
          ),
          enableClickableRow: new UntypedFormControl(
            (<CheckoutContentExtension.ImageBlock>block).enableClickableRow || false,
          ),
          clickableRowUrl: new UntypedFormControl((<CheckoutContentExtension.ImageBlock>block).clickableRowUrl || ''),
        });
      case CheckoutContentExtension.BlockType.DYNAMIC:
      default:
        const defaultDynamicBlock = CheckoutContentExtension.templateDefaultValue
          .blocks[0] as CheckoutContentExtension.DynamicBlock;
        return new UntypedFormGroup({
          title: new UntypedFormControl(
            (<CheckoutContentExtension.DynamicBlock>block).title ?? defaultDynamicBlock.title,
          ),
          bodyText: new UntypedFormControl(
            (<CheckoutContentExtension.DynamicBlock>block).bodyText ?? defaultDynamicBlock.bodyText,
            customDynamicFieldValidator(this.dynamicFieldsAutocompleteOptions$.getValue()),
          ),
          image: new UntypedFormControl(
            getBlockImageSrc(<CheckoutContentExtension.DynamicBlock>block) ?? this.defaultIconUrl,
          ),
          imageContainerWidth: new UntypedFormControl(
            (<CheckoutContentExtension.DynamicBlock>block).imageContainerWidth ??
              defaultDynamicBlock.imageContainerHeight,
          ),
          imageContainerHeight: new UntypedFormControl(
            (<CheckoutContentExtension.DynamicBlock>block).imageContainerHeight ??
              defaultDynamicBlock.imageContainerHeight,
          ),
          type: new UntypedFormControl(CheckoutContentExtension.BlockType.DYNAMIC),
          mode: new UntypedFormControl(
            (<CheckoutContentExtension.DynamicBlock>block).mode ?? CheckoutContentExtension.BlockMode.SIMPLE,
          ),
        });
    }
  }

  dispatchExampleViewValues(values) {
    if (!values) {
      return;
    }

    const template = {
      ...this.formGroup.value,
    };

    template.blocks.forEach((block) => delete block.imageFile);

    const mode = this.loadedExtension ? ViewMode.EDIT : ViewMode.NEW;
    const data = {
      [ExtensionComponentType.CHECKOUT_CONTENT]: {
        mode,
        template,
      },
    };
    this.store.dispatch(CheckoutActions.liveExampleTemplateView({ data }));
  }

  private initChangesForExampleView() {
    const exampleTemplate = CheckoutContentExtension.templateDefaultValue;
    this.dispatchExampleViewValues(this.loadedExtension?.template ?? exampleTemplate);
    this.formGroup.valueChanges.pipe(untilDestroyed(this)).subscribe((values) => {
      this.dispatchExampleViewValues(values);
    });
  }

  onStyleTypeChange(option: DropdownOption) {
    this.formGroup.get('general').get('styleType').setValue(option.value);
  }

  handleBlockTypeChange(option: DropdownOption, index: number) {
    const blocksArray = this.formGroup.get('blocks') as UntypedFormArray;
    blocksArray.removeAt(index);
    blocksArray.insert(index, this.createBlockControl({ type: option.value as CheckoutContentExtension.BlockType }));
  }

  onPreviewChange(imagePreviewBase64: string, index: number) {
    const blocksArr: UntypedFormArray = this.formGroup.get('blocks') as UntypedFormArray;
    const blockControl = blocksArr.at(index);
    blockControl.get('image').setValue(imagePreviewBase64);
  }

  onClearImage(index: number) {
    const blocksArr: UntypedFormArray = this.formGroup.get('blocks') as UntypedFormArray;
    const blockControl = blocksArr.at(index);
    blockControl.get('image').setValue('');
    blockControl.get('image').markAsDirty();
  }

  handleSave() {
    const formValue = this.formGroup.value;

    const template = {
      ...formValue,
    };

    const usageToken = this.usageTokenControl.value;

    const payload: Partial<ICheckoutExtensions> = {
      template,
      usageToken,
      type: ExtensionComponentType.CHECKOUT_CONTENT,
    };
    if (this.loadedExtension?.id) {
      const id = this.loadedExtension.id;
      this.store.dispatch(CheckoutActions.updateExtension({ id, extension: { ...payload, id } }));
    } else {
      this.store.dispatch(CheckoutActions.saveExtension({ extension: payload }));
    }
  }

  private initForm(extension?: ICheckoutExtensions) {
    const templateValue = extension?.template ?? CheckoutContentExtension.templateDefaultValue;
    this.usageTokenControl = new UntypedFormControl(
      extension?.usageToken || 'checkout_content_' + generateRandomString(12),
    );
    this.formGroup = new UntypedFormGroup({
      internalName: new UntypedFormControl(templateValue?.internalName),
      title: new UntypedFormControl(templateValue?.title),
      general: new UntypedFormGroup({
        styleType: new UntypedFormControl(templateValue.general?.styleType),
        border: new UntypedFormControl(templateValue.general?.border),
        borderWidth: new UntypedFormControl(templateValue.general?.borderWidth),
        padding: new UntypedFormControl(templateValue.general?.padding),
        cornerRadius: new UntypedFormControl(templateValue.general?.cornerRadius),
      }),
      numOfBlocks: new UntypedFormControl(templateValue.blocks.length),
      blocks: new UntypedFormArray(templateValue.blocks.map((block) => this.createBlockControl(block))),
      restrictionsRules: this.rulesDynamicFormService.initRulesFormArray(
        templateValue?.restrictionsRules ?? [],
        this.restrictionsRules,
      ),
      placementMode: new UntypedFormControl(templateValue?.placementMode || 'standalone'),
      timerConfiguration: new UntypedFormControl(templateValue?.timerConfiguration || null),
    });

    this.formGroup.statusChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.extensionsService.setFormValid(this.formGroup.valid);
    });
    this.extensionsService.setFormValid(this.formGroup.valid);
  }
  handleClickCheckUrl(url: string) {
    this.dialog.open(IframeDialogComponent, {
      width: '90vw',
      maxWidth: '90vw',
      data: {
        title: 'URL Checker',
        iframeUrl: url,
        iframeHeight: '90vh',
      },
    });
  }

  onTabChange(tabValue: string) {
    this.activeTab = tabValue as CheckoutContentTab;
  }

  get isFormReady(): boolean {
    return !!(this.formGroup && this.usageTokenControl && this.formGroup.controls);
  }

  protected readonly getDynamicColumnOptions = getDynamicColumnOptions;
}
